import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { join } from 'path';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { getCloudGridHome } from './home.js';
import { CloudGridError } from './utils.js';
import type { IV2Client, Grid } from './api/v2-client.js';

/**
 * Server-resolved active org. Always carries an `org_id` because the
 * resolver only returns success when the active slug matched an org in
 * the user's `/api/v2/grids` response — at which point the UUID is known.
 */
export interface ActiveOrg {
  org_id: string;
  slug: string;
  name: string;
}

/**
 * On-disk shape of `~/.cloudgrid/config.yaml`. Server-authoritative
 * fields (orgs, roles) are NOT cached here — they're queried each time
 * via `IV2Client.getOrgs()`. Pre-v0.8 configs may carry a `known_orgs`
 * array; the parser ignores it on read and the writer never re-emits
 * it, so it ages out the next time anything writes the file.
 */
interface ConfigShape {
  api_url?: string;
  domain?: string;
  registry?: string;
  active_org_slug?: string | null;
  last_update_check_at?: string;
  latest_known_version?: string;
  /**
   * Opt-out for the CLI telemetry pipeline (decision 016). `false`
   * disables auto error / success / feedback events. Default `true`;
   * the env var `CLOUDGRID_TELEMETRY` overrides this field at runtime.
   * Persisted via `cloudgrid login --no-telemetry`.
   */
  telemetry?: boolean;
  /**
   * Backlog 072-C — opt-out for the OS-level desktop notification on
   * plug terminal state. Mirrored on `CloudGridConfig` (`config.ts`).
   * Round-tripped here so unrelated mutations (e.g. setActiveOrgSlug)
   * don't silently wipe the user's preference.
   */
  notify_on_deploy?: boolean;
}

function configPath(): string {
  return join(getCloudGridHome(), 'config.yaml');
}

function readConfig(): ConfigShape {
  const p = configPath();
  if (!existsSync(p)) return {};
  try {
    const parsed = (parseYaml(readFileSync(p, 'utf-8')) ?? {}) as Record<string, unknown>;
    // Project to the supported field set. Stray fields (notably the
    // pre-v0.8 `known_orgs` array) are silently dropped here so they
    // never surface to callers.
    return {
      api_url: typeof parsed.api_url === 'string' ? parsed.api_url : undefined,
      domain: typeof parsed.domain === 'string' ? parsed.domain : undefined,
      registry: typeof parsed.registry === 'string' ? parsed.registry : undefined,
      active_org_slug:
        typeof parsed.active_org_slug === 'string'
          ? parsed.active_org_slug
          : parsed.active_org_slug === null
            ? null
            : undefined,
      last_update_check_at:
        typeof parsed.last_update_check_at === 'string' ? parsed.last_update_check_at : undefined,
      latest_known_version:
        typeof parsed.latest_known_version === 'string' ? parsed.latest_known_version : undefined,
      telemetry: typeof parsed.telemetry === 'boolean' ? parsed.telemetry : undefined,
      notify_on_deploy:
        typeof parsed.notify_on_deploy === 'boolean' ? parsed.notify_on_deploy : undefined,
    };
  } catch {
    return {};
  }
}

function writeConfig(cfg: ConfigShape): void {
  const dir = getCloudGridHome();
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
  // Strip undefined keys so the YAML stays compact and so a previously-
  // present `known_orgs` field is not preserved on rewrite.
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cfg)) {
    if (v !== undefined) out[k] = v;
  }
  writeFileSync(configPath(), stringifyYaml(out));
}

export function getActiveOrgSlug(): string | null {
  return readConfig().active_org_slug ?? null;
}

export function setActiveOrgSlug(slug: string): void {
  const cfg = readConfig();
  cfg.active_org_slug = slug;
  writeConfig(cfg);
}

export function clearActiveOrg(): void {
  const cfg = readConfig();
  delete cfg.active_org_slug;
  writeConfig(cfg);
}

export function getLastUpdateCheckAt(): string | null {
  return readConfig().last_update_check_at ?? null;
}

export function setLastUpdateCheck(at: string, version: string): void {
  const cfg = readConfig();
  cfg.last_update_check_at = at;
  cfg.latest_known_version = version;
  writeConfig(cfg);
}

/**
 * Read the persisted telemetry preference. `undefined` means "not set"
 * (default-on); `false` is an explicit opt-out. The env var
 * `CLOUDGRID_TELEMETRY` is checked separately by `isTelemetryEnabled()` —
 * this helper only surfaces what's on disk.
 */
export function getTelemetryConfig(): boolean | undefined {
  return readConfig().telemetry;
}

/**
 * Persist the telemetry preference to `~/.cloudgrid/config.yaml`. Wired
 * from `cloudgrid login --no-telemetry` (sets `false`).
 */
export function setTelemetryConfig(value: boolean): void {
  const cfg = readConfig();
  cfg.telemetry = value;
  writeConfig(cfg);
}

// ── Server-authoritative active-org resolution ────────────────────────────

export interface ResolveActiveOrgOptions {
  /**
   * When set, takes precedence over the persisted `active_org_slug`.
   * Wired from the `--org <slug>` flag on commands that accept it.
   */
  override?: string | null;
}

/**
 * Resolve the active org against the live server-side membership list.
 *
 * Roles and org membership change server-side (admin demotes a user,
 * removes them from an org, adds them to a new one). Caching org
 * metadata locally — as the pre-v0.8 `known_orgs` array did — is the
 * class of bug this resolver exists to close. The local config carries
 * only the user's preference (`active_org_slug`); the server is the
 * authority on what that slug means and whether the user can use it.
 *
 * Throws `CloudGridError` with one of four canonical wordings:
 *   - **No orgs at all** — the user has no memberships.
 *   - **No active slug** — neither `--org` nor `active_org_slug` is set.
 *   - **Stale active slug** — `active_org_slug` doesn't match any
 *     accessible org (e.g. the user was removed from it).
 *   - **`--org` mismatch** — the explicit override slug isn't in the
 *     user's accessible orgs.
 *
 * Auth failures from `IV2Client` (no JWT, expired token) bubble up as
 * `V2ApiError(401, 'AUTH_REQUIRED')` — the caller's normal error mapper
 * picks them up; this resolver does not catch them.
 */
export async function getActiveOrg(
  client: IV2Client,
  options: ResolveActiveOrgOptions = {},
): Promise<ActiveOrg> {
  return (await getActiveOrgWithList(client, options)).activeOrg;
}

/**
 * Like `getActiveOrg` but also returns the full org list fetched during
 * resolution. The list is needed by callers that want to resolve a *link's*
 * `org_id` to its current canonical slug for display (backlog 308) — e.g.
 * `validateLinkVsActiveOrg`'s mismatch message. Fetches `getOrgs()` exactly
 * once, so it's a drop-in for code paths that need both.
 */
export async function getActiveOrgWithList(
  client: IV2Client,
  options: ResolveActiveOrgOptions = {},
): Promise<{ activeOrg: ActiveOrg; orgs: Grid[] }> {
  const orgs = await client.getOrgs();
  const target = options.override ?? readConfig().active_org_slug ?? null;
  const activeOrg = resolveOrThrow(orgs, target, options.override !== undefined);
  return { activeOrg, orgs };
}

/**
 * Soft variant of `getActiveOrg`. Returns `null` when no `active_org_slug`
 * is persisted, so commands like `cloudgrid dev` and `cloudgrid status`
 * (which use the active org only for an optional link-vs-active mismatch
 * check) skip the API call entirely when nothing is configured.
 *
 * When a slug *is* persisted, this defers to the strict resolver — and
 * therefore throws the same friendly error if the slug is stale. That's
 * intentional: a configured-but-invalid active org is a state worth
 * surfacing rather than silently ignoring.
 */
export async function tryGetActiveOrg(client: IV2Client): Promise<ActiveOrg | null> {
  const slug = readConfig().active_org_slug ?? null;
  if (!slug) return null;
  return getActiveOrg(client);
}

function resolveOrThrow(
  orgs: Grid[],
  target: string | null,
  overrideExplicit: boolean,
): ActiveOrg {
  if (orgs.length === 0) {
    throw new CloudGridError(noOrgsMessage());
  }

  const slugs = orgs.map((o) => o.slug);

  if (!target) {
    throw new CloudGridError(noActiveOrgMessage(slugs));
  }

  const match = orgs.find((o) => o.slug === target);
  if (match) {
    return { org_id: match.org_id, slug: match.slug, name: match.name };
  }

  throw new CloudGridError(
    overrideExplicit
      ? overrideNotMemberMessage(target, slugs)
      : staleActiveOrgMessage(target, slugs),
  );
}

// ── Friendly error wording (spec v17 §23 voice rules) ─────────────────────

function formatSlugList(slugs: string[]): string {
  return slugs.join(', ');
}

/**
 * Wording when `active_org_slug` is unset and no `--org` was passed.
 * Lists every accessible slug so the next call has a discoverable target.
 *
 * Exported so commands that detect "no active org" *before* having a
 * resolved org list (e.g. very early validation) can produce the same
 * wording without having to call the resolver. Most callers should
 * prefer `getActiveOrg(client)` and let it throw.
 */
export function noActiveOrgMessage(slugs: string[]): string {
  return [
    'No active grid.',
    `Run \`cloudgrid login\` to pick a default, or pass --grid <slug> on the next call. Available: ${formatSlugList(slugs)}`,
  ].join('\n');
}

/** Wording when `active_org_slug` is set but doesn't match any accessible org. */
function staleActiveOrgMessage(slug: string, available: string[]): string {
  return [
    `Active grid "${slug}" is not in your accessible grids. You may have been removed from it.`,
    `Pass --grid <slug> on the next call. Available: ${formatSlugList(available)}`,
  ].join('\n');
}

/** Wording when `--org <slug>` was explicit but the user isn't a member. */
function overrideNotMemberMessage(slug: string, available: string[]): string {
  return [
    `You are not a member of grid "${slug}".`,
    `Available: ${formatSlugList(available)}`,
  ].join('\n');
}

/** Wording when the user has zero accessible grids. */
function noOrgsMessage(): string {
  return "You don't belong to any grids yet. Create a grid to get started.";
}

/**
 * Canonical "Not signed in" error wording, retained for callers that
 * surface auth failures before any client construction (e.g. `feedback`,
 * `hooks`, `ssh`). v0.8 ships a `cloudgrid login` verb; spec v17 §15
 * names the env-var bypass for non-interactive use.
 */
export function notSignedInMessage(): string {
  return [
    'Not signed in.',
    'Run `cloudgrid login` to authenticate, or set CLOUDGRID_TOKEN for non-interactive use.',
  ].join('\n');
}
